import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useQueue } from '@shared/util/hooks/useQueue';
import { startViewTransition } from '@shared/util/startViewTransition';
import { Toast } from './Toast';
import { ToastContext } from './ToastContext';
import { QueuedToast, useToastCreator } from './useToastCreator';
import styles from './ToastProvider.module.css';

export function ToastProvider({ children }: { children: ReactNode }) {
  const queue = useQueue<QueuedToast>();
  const toast = useToastCreator(queue);
  const value = useMemo(() => ({ toast }), [toast]);
  const [items, setItems] = useState<QueuedToast[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current) {
      startViewTransition(() => {
        setItems(queue.items);
      }, containerRef.current);
    }
  }, [queue.items]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className={styles.container} ref={containerRef}>
        <div className={styles.topContainer}>
          {items.filter(isPositionTop).map((t, index) => (
            <ToastRenderer key={t.id} toast={t} index={index} />
          ))}
        </div>
        <div className={styles.bottomContainer}>
          {items.filter(isPositionBottom).map((t, index) => (
            <ToastRenderer key={t.id} toast={t} index={index} />
          ))}
        </div>
      </div>
    </ToastContext.Provider>
  );
}

function isPositionTop(toast: QueuedToast) {
  return toast.position === 'top';
}

function isPositionBottom(toast: QueuedToast) {
  return toast.position === 'bottom';
}

function ToastRenderer({ toast, index }: { toast: QueuedToast; index: number }) {
  const { remove, ...toastProps } = toast;
  return <Toast {...toastProps} onClose={remove} autoClose={toastProps.autoClose && index === 0} />;
}
